import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { URL_PLAYER, URL_VIDEODETAIL } from "../Routes/Location";
import { renderImageUrl, getWidgetTitle, getPreviewUrl } from "../../Utils/utils";
import defaultBanner from "@app/Resources/Images/default_banner.png";
import {
  STATE_LIVE,
  STATE_UPCOMING,
  STATE_SUMMARY,
  getContentState,
  splitVsTitle,
  getCategoryName,
  formatMeta,
} from "./featuredUtils";
import style from "./style.module.css";

const AUTOPLAY_MS = 7000;
const PREVIEW_HOVER_MS = 1000;
const SWIPE_PX = 50;
const DRAG_PX = 5;
const STATE_RECALC_MS = 60000;
const MAX_SEGMENTS = 10;
const MIN_TITLE_SCALE = 0.5;
const TITLE_FIT_MARGIN = 8;
// el máximo del degradado empieza un poco por debajo del inicio del texto
const FADE_OFFSET = 12;

const STATE_LABEL = {
  [STATE_LIVE]: ["highlight.state.live", "En directo"],
  [STATE_UPCOMING]: ["highlight.state.upcoming", "Próximo"],
  [STATE_SUMMARY]: ["highlight.state.summary", "Resumen"],
};
const CTA_LABEL = {
  [STATE_LIVE]: ["highlight.cta.live", "Ver en directo"],
  [STATE_SUMMARY]: ["highlight.cta.summary", "Ver resumen"],
};
const DEFERRED_CTA_LABEL = ["global.button.watch", "Ver"];

const mainImage = (w) => w.background || w.banner || null;
const thumbImage = (w) => w.banner || w.background || null;

const Arrow = ({ dir, onClick, label }) => (
  <button type="button" className={style.arrow} onClick={onClick} aria-label={label}>
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d={dir < 0 ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
);

const WidgetHighlight_v2 = ({ playlist, setRef, trigger, clickCallback, rotateTime }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const lang = i18n.language?.split("-")[0];
  const items = playlist ?? [];
  const total = items.length;
  const [index, setIndex] = useState(0);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [paused, setPaused] = useState(false);
  const showPreviews = useSelector((state) => state.commonState.previews);
  const previewCache = useRef({});
  const previewTimer = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const heroRef = useRef(null);
  const [heroW, setHeroW] = useState(1200);
  const drag = useRef({ down: false, moved: false, x: 0, y: 0 });
  const measureRef = useRef(null);
  const [textMaxH, setTextMaxH] = useState(0);

  const go = useCallback((i) => setIndex(((i % total) + total) % total), [total]);

  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), STATE_RECALC_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const el = heroRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    // Se aplaza al siguiente frame: actualizar el estado dentro del callback cambia el layout
    // y provoca "ResizeObserver loop completed with undelivered notifications"
    let raf;
    const ro = new ResizeObserver(([e]) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setHeroW(e.contentRect.width));
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
    // eslint-disable-next-line
  }, [total > 0]);

  // Alto del bloque de texto más alto de todos los items (con los títulos ya recortados con elipsis),
  // para que en móvil el botón quede justo debajo y en la misma posición en todo el carrusel
  // Si ese bloque no cabe dentro de la imagen, se reduce el título de todos los items hasta que quepa
  const measureText = useCallback(() => {
    const el = measureRef.current;
    const hero = heroRef.current;
    if (!el || !hero) return;
    const maxH = () => Math.max(0, ...Array.from(el.children, (c) => c.offsetHeight));
    const text = el.parentElement.querySelector(`:scope > .${style.textInfo}`);
    const content = el.parentElement.parentElement;
    let max;
    if (text) {
      // el bloque está anclado abajo: su borde inferior no cambia al cambiar el tamaño del título
      const textBottom = text.getBoundingClientRect().bottom;
      const top = content.getBoundingClientRect().top + parseFloat(getComputedStyle(content).paddingTop) + TITLE_FIT_MARGIN;
      let scale = 1;
      hero.style.setProperty("--title-scale", scale);
      max = maxH();
      while (textBottom - max < top && scale > MIN_TITLE_SCALE) {
        scale = Math.max(MIN_TITLE_SCALE, Math.round((scale - 0.05) * 100) / 100);
        hero.style.setProperty("--title-scale", scale);
        max = maxH();
      }
    } else {
      max = maxH();
    }
    setTextMaxH((h) => (h === max ? h : max));
  }, []);

  useLayoutEffect(measureText, [measureText, playlist, heroW, now, lang]);

  // El degradado llega al máximo un poco por debajo de donde empieza el texto del item actual: más alto cuanto más líneas tiene el título
  useLayoutEffect(() => {
    const hero = heroRef.current;
    const text = measureRef.current?.parentElement.querySelector(`:scope > .${style.textInfo}`);
    const first = text?.firstElementChild;
    if (!hero || !first) return;
    const fadeH = Math.round(hero.getBoundingClientRect().bottom - first.getBoundingClientRect().top - FADE_OFFSET);
    // la primera vez sin transición, para que el degradado no crezca desde 0 al cargar
    const initial = !hero.style.getPropertyValue("--fade-h");
    if (initial) hero.style.transition = "none";
    hero.style.setProperty("--fade-h", `${fadeH}px`);
    if (initial) {
      hero.getBoundingClientRect();
      hero.style.transition = "";
    }
  });

  useEffect(() => {
    const el = measureRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    // También cubre el cambio de alto cuando terminan de cargar las fuentes
    let raf;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measureText);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
    // eslint-disable-next-line
  }, [measureText, total > 0]);

  useEffect(() => {
    if (index >= total) setIndex(0);
  }, [total, index]);

  // Preview: con el cursor encima durante un tiempo se reproduce, como en el widget anterior
  const currentId = items[index]?.id;
  useEffect(() => {
    setPreviewUrl(null);
    if (!paused || !showPreviews || currentId == null) return undefined;
    previewTimer.current = setTimeout(async () => {
      if (!(currentId in previewCache.current)) {
        previewCache.current[currentId] = (await getPreviewUrl(currentId)) || null;
      }
      setPreviewUrl(previewCache.current[currentId]);
    }, PREVIEW_HOVER_MS);
    return () => clearTimeout(previewTimer.current);
  }, [paused, showPreviews, currentId]);

  useEffect(() => {
    if (total < 2 || paused) return undefined;
    const id = setTimeout(() => go(index + 1), rotateTime || AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [index, total, paused, rotateTime, go]);

  if (!total) return null;
  const w = items[index] ?? items[0];

  const onPointerDown = (e) => {
    drag.current = { down: true, moved: false, x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e) => {
    if (drag.current.down && Math.max(Math.abs(e.clientX - drag.current.x), Math.abs(e.clientY - drag.current.y)) > DRAG_PX) {
      drag.current.moved = true;
    }
  };
  const onPointerUp = (e) => {
    if (!drag.current.down) return;
    drag.current.down = false;
    const dx = e.clientX - drag.current.x;
    if (Math.abs(dx) > SWIPE_PX) go(index + (dx < 0 ? 1 : -1));
  };
  // Arrastrar nunca activa el trigger.
  const swallowDragClick = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      drag.current.moved = false;
    }
  };

  const isMobile = heroW < 768;

  const renderText = (it) => {
    const itState = getContentState(it, now);
    const itTitle = getWidgetTitle(it) ?? "";
    const teams = splitVsTitle(itTitle);
    const sport = getCategoryName(it, lang);
    const meta = formatMeta(it, itState, lang);
    const label = STATE_LABEL[itState];
    const badge = label && (
      <span className={`${style.badge} ${itState === STATE_LIVE ? style.badgeLive : ""}`}>
        {itState === STATE_LIVE && "● "}
        {t(label[0], label[1])}
      </span>
    );
    const sportTag = sport && <span className={style.sport}>{sport}</span>;
    const title = teams ? (
      <h2 className={style.title}>
        <span className={style.team}>{teams[0]}</span>
        <span className={style.vs}>vs</span>
        <span className={style.team}>{teams[1]}</span>
      </h2>
    ) : (
      <h2 className={`${style.title} ${style.titlePlain}`}>{itTitle}</h2>
    );

    // La categoría va en la línea de la fecha para ahorrar alto; en móvil también el estado, bajo el título
    return (
      <>
        {!isMobile && badge && <div className={style.topLine}>{badge}</div>}
        {title}
        {((isMobile && badge) || sportTag || meta) && (
          <div className={`${style.topLine} ${style.meta}`}>
            {isMobile && badge}
            {sportTag}
            {sportTag && meta && <span className={style.metaText} aria-hidden="true">·</span>}
            {meta && <span className={style.metaText}>{meta}</span>}
          </div>
        )}
      </>
    );
  };

  const state = getContentState(w, now);
  // En móvil el fondo panorámico (3,84:1) queda en una franja muy baja: se usa el banner 16:9
  const img = isMobile ? thumbImage(w) : mainImage(w);
  const cta = CTA_LABEL[state];
  const detailUrl = `${URL_VIDEODETAIL}/${w.id}`;
  const playUrl = `${URL_PLAYER}/${w.id}`;
  const useSegments = total <= MAX_SEGMENTS;
  const thumbW = 130;
  // En tablet la imagen es demasiado baja para que las miniaturas quepan dentro
  const maxThumbs = heroW >= 1024 ? 6 : 0;
  const nThumbs = Math.min(maxThumbs, total, Math.floor((0.45 * heroW + 12) / (thumbW + 12)));
  const showThumbs = nThumbs >= 2;
  // Las miniaturas no rotan: la ventana solo avanza por páginas cuando el activo sale de ella
  const thumbStart = showThumbs ? Math.min(Math.floor(index / nThumbs) * nThumbs, total - nThumbs) : 0;
  const thumbs = Array.from({ length: showThumbs ? nThumbs : 0 }, (_, k) => {
    const i = thumbStart + k;
    return { it: items[i], i };
  });
  const duration = rotateTime || AUTOPLAY_MS;
  const progressFill = (className) => (
    <span
      key={`${index}-${now > 0}`}
      className={className}
      style={{ animationDuration: `${duration}ms`, animationPlayState: paused ? "paused" : "running" }}
    />
  );

  return (
    <section
      ref={heroRef}
      className={`${style.hero} ${showThumbs ? style.withThumbs : ""}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClickCapture={swallowDragClick}
      onClick={(e) => {
        // Cualquier zona del destacado abre la ficha, salvo enlaces y botones propios
        if (e.defaultPrevented || e.target.closest("a, button")) return;
        history.push(detailUrl);
      }}
      aria-roledescription="carousel"
    >
      <div className={`${style.media} ${img ? "" : style.mediaFallback}`}>
        {previewUrl ? (
          <video
            key={`preview-${w.id}`}
            className={`${style.image} ${style.previewVideo}`}
            muted
            autoPlay
            loop
            playsInline
            onError={(e) => {
              e.target.pause();
              setPreviewUrl(null);
            }}
          >
            <source src={previewUrl} type="video/webm" />
            <source src={previewUrl} type="video/mp4" />
          </video>
        ) : (
          <img
            key={w.id}
            className={style.image}
            src={img ? renderImageUrl(img) : defaultBanner}
            alt=""
            draggable={false}
            ref={setRef ? setRef(w.title) : undefined}
          />
        )}
      </div>

      <div className={style.content}>
        <div className={style.textCol}>
          <div className={style.measure} ref={measureRef} aria-hidden="true">
            {items.map((it) => (
              <div key={it.id} className={style.textInfo}>
                {renderText(it)}
              </div>
            ))}
          </div>
          <div className={style.textInfo} style={textMaxH ? { "--text-max-h": `${textMaxH}px` } : undefined}>
            {renderText(w)}
          </div>

          <div className={style.buttons}>
            {cta && (
              <Link
                to={playUrl}
                className={style.cta}
                onClick={clickCallback ? () => clickCallback(w.id) : undefined}
              >
                <span className={style.play} aria-hidden="true" />
                {t(cta[0], cta[1])}
              </Link>
            )}
            {!cta && state !== STATE_UPCOMING && (
              <Link to={playUrl} className={style.cta} onClick={clickCallback ? () => clickCallback(w.id) : undefined}>
                <span className={style.play} aria-hidden="true" />
                {t(DEFERRED_CTA_LABEL[0], DEFERRED_CTA_LABEL[1])}
              </Link>
            )}
            <Link
              to={detailUrl}
              className={`${style.info} ${state === STATE_UPCOMING ? style.infoText : ""}`}
              aria-label={t("content.more.information")}
            >
              <span className={style.infoIcon}>i</span>
              <span className={style.infoLabel}>{t("content.more.information")}</span>
            </Link>
            {/* En móvil las flechas van en la fila de los botones y el progreso queda solo debajo */}
            {isMobile && total > 1 && (
              <>
                <Arrow dir={-1} label={t("carousel.previous")} onClick={() => go(index - 1)} />
                <Arrow dir={1} label={t("carousel.next")} onClick={() => go(index + 1)} />
              </>
            )}
          </div>
        </div>

        {total > 1 && (
          <div className={style.controls}>
            {showThumbs && (
              <div className={style.navTop}>
                <div className={style.arrows}>
                  <Arrow dir={-1} label={t("carousel.previous")} onClick={() => go(index - 1)} />
                  <Arrow dir={1} label={t("carousel.next")} onClick={() => go(index + 1)} />
                </div>
              </div>
            )}
            {showThumbs && (
              <div className={style.thumbs}>
                {thumbs.map(({ it, i }, k) => (
                  <button key={it.id} type="button" className={`${style.thumb} ${i === index ? style.thumbActive : ""}`} onClick={() => go(i)}>
                    <span className={style.thumbProgress}>
                      {i === index && progressFill(style.thumbFill)}
                    </span>
                    <span className={style.thumbImg}>
                      <img src={renderImageUrl(thumbImage(it) || defaultBanner)} alt="" draggable={false} />
                      <span className={style.thumbSport}>{getCategoryName(it, lang)}</span>
                    </span>
                    <span className={style.thumbTitle}>{getWidgetTitle(it)}</span>
                  </button>
                ))}
              </div>
            )}
            {!showThumbs && (
              <div className={style.nav}>
                <div className={style.progress}>
                  {useSegments
                    ? items.map((it, i) => (
                        <span key={it.id} className={`${style.segment} ${i < index ? style.segmentActive : ""}`}>
                          {i === index && progressFill(style.progressFill)}
                        </span>
                      ))
                    : items.map((it, i) => (
                        <span key={it.id} className={`${style.dot} ${i === index ? style.dotActive : i < index ? style.dotSeen : ""}`}>
                          {i === index && progressFill(style.progressFill)}
                        </span>
                      ))}
                </div>
                {!isMobile && (
                  <>
                    <Arrow dir={-1} label={t("carousel.previous")} onClick={() => go(index - 1)} />
                    <Arrow dir={1} label={t("carousel.next")} onClick={() => go(index + 1)} />
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default WidgetHighlight_v2;
